import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Paperclip, Loader2, GraduationCap, Pencil } from "lucide-react";
import { toast } from "sonner";
import { AttachmentsDialog, GroupPicker } from "./Attachments";

const EV_TYPES = ["Exercice", "Interrogation", "Devoir", "Contrôle", "Participation", "Autre"];
const EV_STATUS = ["Brouillon", "Publié interne", "Archivé"];
const ATT_TYPES = [
  { v: "evaluation_subject", l: "Sujet" }, { v: "evaluation_correction", l: "Corrigé" },
  { v: "grading_rubric", l: "Barème" }, { v: "other", l: "Autre" },
];

export default function Evaluations() {
  const [group, setGroup] = useState("");
  const [rows, setRows] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [att, setAtt] = useState(null);
  const [grid, setGrid] = useState(null); // {evaluation, max_score, students}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!group) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/pedagogy/evaluations?group_id=${group}`); setRows(data);
      const s = await api.get(`/pedagogy/groups/${group}/subjects`); setSubjects(s.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [group]);

  const openNew = () => setForm({ title: "", subject_id: subjects[0]?.id || "", date: new Date().toISOString().slice(0, 10), coefficient: 1, max_score: 20, type: "Contrôle", status: "Brouillon" });
  const save = async () => {
    if (!form.title) { toast.error("Titre requis"); return; }
    setBusy(true);
    try { await api.post("/pedagogy/evaluations", { ...form, group_id: group }); toast.success("Évaluation créée."); setForm(null); await load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };

  const openGrid = async (ev) => {
    try { const { data } = await api.get(`/pedagogy/evaluations/${ev.id}/grades`); setGrid({ ...data, id: ev.id }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
  };
  const setScore = (sid, v) => setGrid((g) => ({ ...g, students: g.students.map((s) => s.student_id === sid ? { ...s, score: v } : s) }));
  const toggleAbsent = (sid) => setGrid((g) => ({ ...g, students: g.students.map((s) => s.student_id === sid ? { ...s, absent: !s.absent } : s) }));
  const saveGrid = async () => {
    setBusy(true);
    try {
      await api.post(`/pedagogy/evaluations/${grid.id}/grades`,
        { records: grid.students.map((s) => ({ student_id: s.student_id, score: s.score, comment: s.comment || "", absent: !!s.absent, not_graded: !!s.not_graded })) });
      toast.success("Notes enregistrées."); setGrid(null); await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="pedagogy-grades-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-display font-bold text-slate-900 flex items-center gap-2"><GraduationCap className="h-6 w-6 text-primary" />Notes & évaluations</h1>
        <div className="flex items-center gap-2">
          <GroupPicker value={group} onChange={setGroup} testid="ev-group" />
          <Button onClick={openNew} disabled={!group} className="gap-2 rounded-full" data-testid="ev-new"><Plus className="h-4 w-4" />Nouvelle évaluation</Button>
        </div>
      </div>
      <Card className="border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70">
            <TableHead>Titre</TableHead><TableHead>Matière</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead>
            <TableHead>Bar.</TableHead><TableHead>Coef.</TableHead><TableHead>Notes</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" /></TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Aucune évaluation.</TableCell></TableRow>
                : rows.map((e) => (
                  <TableRow key={e.id} data-testid="ev-row">
                    <TableCell className="font-medium">{e.title}</TableCell>
                    <TableCell>{e.subject_name || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{e.date}</TableCell>
                    <TableCell><Badge variant="secondary">{e.type}</Badge></TableCell>
                    <TableCell>{e.max_score}</TableCell><TableCell>{e.coefficient}</TableCell>
                    <TableCell><Badge variant="secondary">{e.grades_count}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openGrid(e)} data-testid={`ev-grid-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setAtt(e)} data-testid={`ev-att-${e.id}`}><Paperclip className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="ev-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouvelle évaluation</DialogTitle></DialogHeader>
          {form && <div className="space-y-3">
            <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ev-title" />
            <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
              <SelectTrigger data-testid="ev-subject"><SelectValue placeholder="Matière" /></SelectTrigger>
              <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-2">
              <div className="flex-1"><label className="text-xs text-slate-500">Date</label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="w-24"><label className="text-xs text-slate-500">Barème</label><Input type="number" value={form.max_score} onChange={(e) => setForm({ ...form, max_score: e.target.value })} data-testid="ev-max" /></div>
              <div className="w-24"><label className="text-xs text-slate-500">Coef.</label><Input type="number" value={form.coefficient} onChange={(e) => setForm({ ...form, coefficient: e.target.value })} /></div>
            </div>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{EV_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{EV_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Annuler</Button><Button onClick={save} disabled={busy} data-testid="ev-save">{busy ? "…" : "Créer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!grid} onOpenChange={(o) => !o && setGrid(null)}>
        <DialogContent className="max-w-lg" data-testid="grades-grid">
          <DialogHeader><DialogTitle className="font-display">Saisie des notes — /{grid?.max_score}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {grid?.students?.length === 0 ? <p className="text-sm text-slate-400 py-3 text-center">Aucun élève.</p>
              : grid?.students?.map((s) => (
                <div key={s.student_id} className="flex items-center gap-2 border-b pb-2" data-testid="grade-row">
                  <span className="flex-1 text-sm text-slate-700">{s.name}</span>
                  <Input type="number" className="w-20" value={s.absent ? "" : (s.score ?? "")} disabled={s.absent}
                    onChange={(e) => setScore(s.student_id, e.target.value)} data-testid={`grade-input-${s.student_id}`} placeholder="—" />
                  <button onClick={() => toggleAbsent(s.student_id)} className={`text-xs px-2 py-1 rounded-full ${s.absent ? "bg-red-100 text-red-700" : "bg-slate-50 text-slate-400"}`}>Abs.</button>
                </div>
              ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setGrid(null)}>Annuler</Button><Button onClick={saveGrid} disabled={busy} data-testid="grades-save">{busy ? "…" : "Enregistrer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachmentsDialog open={!!att} onClose={() => setAtt(null)} parentType="evaluation" parentId={att?.id} types={ATT_TYPES} />
    </div>
  );
}
